import { useState, useEffect, useRef, useCallback } from 'react';
import { useSimStore } from '../../store/useSimStore';
import {
  INTERVENTIONS,
  CAPNO_MORPHOLOGIES,
  computeInterventionEffect,
  revertAppliedDeltas,
  computeVentTargets,
  defaultDose,
  VENT_ABSOLUTE_KEYS,
  getInterventionCapnoMorphology,
  getInterventionRhythm,
  isInterventionRelevant,
  isRecoveryBlocked,
  recoveryGateStatus,
  formatCountdown,
} from '../../engine/interventionEngine';
import { SCENARIOS } from '../../engine/scenarioEngine';
import { shockDelayMs } from '../../engine/alarmEngine';
import HelpTip from './HelpTip';
import styles from './InterventionPanel.module.css';

const SHOCK_SCENARIOS = new Set(['synchronizedCardioversion', 'defibrillation']);

// Selectable shock energy presets (Joules, biphasic). Defibrillation escalates
// 120→360 J; synchronised cardioversion is rhythm-dependent (≈50–200 J). The
// operator picks the energy before delivering, and it is shown on the shock log,
// the monitor tally and the case recording.
const SHOCK_ENERGY = {
  defibrillation: [120, 150, 200, 300, 360],
  synchronizedCardioversion: [50, 75, 100, 120, 150, 200],
};
const DEFAULT_SHOCK_ENERGY = { defibrillation: 200, synchronizedCardioversion: 100 };

// CPR modes. While one is active the ECG shows compression artifact, hiding the
// patient's real rhythm — so the instructor can call a "rhythm check" to pause
// compressions and reveal it (see doRhythmCheck).
const CPR_KEYS = new Set(['cprHighQuality', 'cprLowQuality', 'lucasCPR']);
const RHYTHM_CHECK_MS = 6000;   // ~6 s pulse/rhythm check, then compressions resume

// Human-readable rhythm names for the rhythm-check banner + case log.
const RHYTHM_LABEL = {
  normal: 'Organised sinus rhythm', vf: 'Ventricular fibrillation',
  vtach: 'Ventricular tachycardia', vtach_pulseless: 'Pulseless VT',
  asystole: 'Asystole', tdp: 'Torsades de pointes', afib: 'Atrial fibrillation',
  svt: 'SVT', hyperkalemia: 'Hyperkalaemic sine-wave', paced: 'Paced rhythm',
  st_elevation: 'ST elevation', st_depression: 'ST depression',
  hypothermia: 'Osborn (J) waves', tamponade: 'Electrical alternans',
  cpr_mech: 'Mechanical CPR artifact', iabp: 'IABP-augmented', impella: 'Impella flow',
};
const rhythmLabel = (r) => RHYTHM_LABEL[r] ?? r;

/** Shockable rhythms — a rhythm check on these means "shock again". */
const SHOCKABLE = new Set(['vf', 'vtach', 'vtach_pulseless', 'tdp']);

// Major procedures — logged to the case record as kind 'procedure' (distinct
// from drugs) so they stand out on the debrief / replay timeline.
const PROCEDURE_KEYS = new Set([
  'cricothyrotomy', 'needleDecompression', 'pericardiocentesis', 'intubation',
  'transcutaneousPacemaker', 'perimortemHysterotomy',
  'thoracostomy', 'chestTube', 'pelvicBinder', 'irEmbolization', 'massiveTransfusion',
  'reboa', 'dialysis', 'bronchoscopy', 'passiveLegRaise', 'vagalManeuver',
  'recruitmentManeuver', 'proning', 'durantsManeuver', 'leftUterineDisplacement', 'headUpPosition',
]);
const eventKindFor = (key) => (PROCEDURE_KEYS.has(key) ? 'procedure' : 'intervention');

// Pseudo-scenarios this panel applies itself (shocks, CPR modes, VA-ECMO). They
// drive rhythm/vitals but must NOT re-anchor the drug-relevance palette — that
// stays pinned to the last patient scenario chosen in the Scenario Selector.
const PANEL_SCENARIO_KEYS = new Set([
  'synchronizedCardioversion', 'defibrillation',
  'cprHighQuality', 'cprLowQuality', 'lucasCPR', 'vaECMO', 'iabp', 'impella',
]);

/**
 * InterventionPanel
 *
 * Timed pharmacological / fluid buttons.  Each shows a live countdown
 * while active and auto-reverts on expiry.
 *
 * Expiry logic: the effective deltas applied at administration time are
 * captured in a ref and reversed (sign = −1) via useSimStore.getState()
 * at timeout, so the revert always works against the live vital targets
 * rather than a stale closure.
 */
// ── TOF / BIS drug effect tables ─────────────────────────────────────────────
// TOF: { count: 0-4, ratio: 0-100 } at full block (when drug is active)
const TOF_EFFECTS = {
  succinylcholine: { count: 0, ratio: 0 },   // phase I depolarising block
  rocuronium:      { count: 0, ratio: 0 },   // non-depolarising, RSI dose
  cisatracurium:   { count: 0, ratio: 0 },   // non-depolarising, surgical block
  sugammadex:      { count: 4, ratio: 95 },  // encapsulates rocuronium → near-full reversal
  neostigmine:     { count: 4, ratio: 80 },  // anticholinesterase → partial reversal
};

// BIS offsets for EEG-slowing sedatives (additive, applied first)
const STATE_KEY = 'sim.interventionState';
const STATE_MAX_AGE_MS = 2 * 60 * 60 * 1000;   // a snapshot older than 2 h is a new session

const BIS_EFFECTS = {
  propofol:        -45,  // 98 → ~53 at therapeutic dose (GA band 40–60)
  dexmedetomidine: -22,  // sedation without full anaesthesia
  sevoflurane:     -48,  // volatile at 1 MAC → ~50 (solid GA band 40–60)
  desflurane:      -48,
  isoflurane:      -48,
  nitrousOxide:    -10,  // 50 % N₂O alone: light sedation only
  midazolam:       -25,  // benzodiazepine sedation (EEG slowing)
  lorazepam:       -22,
};

// Ketamine is modelled separately. As a dissociative it produces high-frequency
// EEG activation, which the BIS algorithm misreads as arousal — so BIS is
// UNRELIABLE under ketamine. Clinically it lowers an awake BIS only modestly,
// but when added to a deep anaesthetic it paradoxically RAISES the reading: a
// BIS of 30–40 (propofol ± dexmedetomidine) climbs into the 60s.
//   final = max(baseBIS + offset, floor)
const KETAMINE_BIS = { offset: -16, floor: 62 };

export default function InterventionPanel() {
  const store = useSimStore();
  const capnoMorphology = useSimStore((state) => state.capnoMorphology);
  const setCapnoMorphology = useSimStore((state) => state.setCapnoMorphology);
  const setRhythm = useSimStore((state) => state.setRhythm);
  const activeScenario = useSimStore((state) => state.activeScenario);
  const transitionDuration = useSimStore((state) => state.transitionDuration);
  const customScenarios = useSimStore((state) => state.customScenarios);
  const setTof = useSimStore((state) => state.setTof);
  const setBIS = useSimStore((state) => state.setBIS);

  // Selected energy (J) per shock type — set before delivering. Declared here
  // (before handleScenario) because handleScenario closes over it in its deps.
  const [shockEnergy, setShockEnergy] = useState(DEFAULT_SHOCK_ENERGY);

  // ── Rhythm check (during CPR) ──────────────────────────────────────────────
  // While a CPR mode runs, the ECG is compression artifact. `underlyingRhythm`
  // remembers the patient's true rhythm beneath it — captured when compressions
  // start, and updated (instead of the visible ECG) whenever a drug would change
  // the rhythm mid-CPR. A refractory scenario whose cause is untreated therefore
  // still reveals its abnormal rhythm on the next check, even after shocks.
  const [underlyingRhythm, setUnderlyingRhythm] = useState(null);
  const [rhythmCheckUntil, setRhythmCheckUntil] = useState(0);
  const cprActive = CPR_KEYS.has(activeScenario);
  const cprActiveRef = useRef(cprActive);
  cprActiveRef.current = cprActive;
  const rhythmCheckTimerRef = useRef(null);

  // Switch the ECG immediately (no cross-fade): a rhythm check reveals the
  // underlying rhythm the instant compressions pause.
  const setRhythmInstant = useCallback((r) => {
    store.setRhythm(r);
    store.setRhythmProgress(1);
  }, [store]);

  // ── Apply a scenario (used for Special Equipment procedure buttons) ──────────
  const applyScenario = useCallback((scenarioKey) => {
    const scenario = SCENARIOS[scenarioKey];
    if (!scenario) return;
    store.setRhythm(scenario.rhythm || 'normal');
    store.setCapnoMorphology(scenario.capnoMorphology || 'normal');
    store.setActiveScenario(scenarioKey);
    store.applyVitals(scenario.vitals);
    // Reveal the channels the state's key findings live on (e.g. IABP/Impella →
    // arterial line, where their signature waveforms show). Same behaviour as
    // ScenarioSelector; instructor can still toggle any channel off.
    if (scenario.channels) {
      Object.entries(scenario.channels).forEach(([c, visible]) => store.setChannelVisible(c, visible));
    }
  }, [store]);

  const handleScenario = useCallback((scenarioKey) => {
    const name = SCENARIOS[scenarioKey]?.name || scenarioKey;
    if (SHOCK_SCENARIOS.has(scenarioKey)) {
      const shockType = scenarioKey === 'defibrillation' ? 'defibrillation' : 'cardioversion';
      const energy = shockEnergy[scenarioKey] ?? DEFAULT_SHOCK_ENERGY[scenarioKey];
      // Gated recovery: the shock is still DELIVERED (audio + log) even when the
      // cause is untreated — but it achieves no ROSC. The patient stays in arrest.
      const gate = SCENARIOS[relevanceCtxRef.current.scenario]?.recoveryGate ?? null;
      const blocked = isRecoveryBlocked(gate, scenarioKey, antidoteTimesRef.current, Date.now(), gateSettleMs(gate));
      // Record the shock time (newest first). Logged on the button press — the
      // moment the operator delivers the shock — not the delayed vitals change.
      setShockLog((log) => [{ type: shockType, t: Date.now(), energy }, ...log].slice(0, 30));
      store.recordCaseEvent('shock', `${blocked ? `${name} — no ROSC (refractory)` : name} · ${energy} J`);
      // Fire the audio cue — the MONITOR window plays the charge→shock sequence.
      // Energy rides along so the monitor's shock tally can display it.
      store.setAudioCue({ type: shockType, id: Date.now(), energy });
      if (blocked) {
        const st = recoveryGateStatus(gate, antidoteTimesRef.current, Date.now(), gateSettleMs(gate));
        setGateNotice({
          text: st.anyGiven
            ? `No ROSC — antidote still circulating (${formatCountdown(st.settleRemainingMs)} to effect). ${gate.hint}`
            : `No ROSC — ${gate.hint}`,
          t: Date.now(),
        });
      } else {
        // Time the post-shock vitals change to the synchronised shock moment.
        setTimeout(() => applyScenario(scenarioKey), shockDelayMs(shockType));
        // ROSC: the rhythm beneath any subsequent compressions is now organised.
        setUnderlyingRhythm(SCENARIOS[scenarioKey]?.rhythm || 'normal');
      }
      // NOTE: when `blocked`, nothing is applied — so `underlyingRhythm` keeps the
      // abnormal (still-shockable) rhythm and the next rhythm check reveals it.
    } else {
      store.recordCaseEvent('intervention', name);   // CPR mode / VA-ECMO
      // Starting compressions hides the real rhythm — remember it so a rhythm
      // check can reveal it. (Re-selecting a CPR mode must not overwrite it.)
      if (CPR_KEYS.has(scenarioKey)) {
        const cur = useSimStore.getState().rhythm;
        if (!['cpr_hq', 'cpr_lq', 'cpr_mech'].includes(cur)) setUnderlyingRhythm(cur);
      }
      applyScenario(scenarioKey);
    }
  }, [store, applyScenario, shockEnergy]);

  // Pause compressions for ~6 s to reveal the underlying rhythm, then resume.
  const doRhythmCheck = useCallback(() => {
    const live = useSimStore.getState();
    const cprRhythm = live.rhythm;
    if (!['cpr_hq', 'cpr_lq', 'cpr_mech'].includes(cprRhythm)) return;   // already checking
    const reveal = underlyingRhythm ?? 'asystole';
    setRhythmInstant(reveal);
    live.recordCaseEvent('procedure', `Rhythm check — ${rhythmLabel(reveal)}`);
    setRhythmCheckUntil(Date.now() + RHYTHM_CHECK_MS);

    clearTimeout(rhythmCheckTimerRef.current);
    rhythmCheckTimerRef.current = setTimeout(() => {
      // Only resume compressions if the instructor hasn't moved on meanwhile.
      if (useSimStore.getState().rhythm === reveal) setRhythmInstant(cprRhythm);
      setRhythmCheckUntil(0);
    }, RHYTHM_CHECK_MS);
  }, [underlyingRhythm, setRhythmInstant]);

  // Track which groups are open. All open by default; click header to toggle.
  const [openGroups, setOpenGroups] = useState(
    () => new Set(['Capnogram Morphology', 'CPR & Resuscitation', 'Fluids & Hemostasis', 'Vasopressors & Inotropes',
      'Antiarrhythmics & Rate Control', 'Antihypertensives & Vasodilators', 'Endocrine & Glucose', 'Bronchodilators', 'Obstetric',
      'Electrolytes & Resuscitation', 'Airway & Ventilation', 'Maneuvers & Positioning',
      'Analgesics', 'Sedatives', 'Benzodiazepines', 'Paralytics', 'Reversal Agents',
      'Other Medications', 'Special Equipment'])
  );

  // Declutter: when a scenario is active, hide interventions not indicated for it.
  const [showAllDrugs, setShowAllDrugs] = useState(false);

  // Timestamped shock log (cardioversion / defibrillation). Newest first.
  const [shockLog, setShockLog] = useState([]);

  // Compact drug cards: collapsed = name only; the chevron expands ONE card in
  // place to reveal description + onset/duration. Set of intervention keys.
  const [expandedCards, setExpandedCards] = useState(() => new Set());
  const toggleCard = useCallback((key) => {
    setExpandedCards((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  }, []);

  // ── Frozen relevance context ────────────────────────────────────────────────
  // The drug palette is filtered to what's indicated for the SELECTED PATIENT
  // SCENARIO and must stay stable while interventions run. Two things would
  // otherwise disturb it: (1) the live vitals an intervention changes, and (2)
  // the shock/CPR buttons here, which internally set activeScenario. So we
  // snapshot {scenario, modifier, vitals} and refresh it ONLY when the scenario/
  // modifier changes via the Scenario Selector — never for this panel's own
  // pseudo-scenarios (PANEL_SCENARIO_KEYS) or ongoing vital changes.
  const [relevanceCtx, setRelevanceCtx] = useState(() => ({
    scenario: useSimStore.getState().activeScenario,
    modifier: useSimStore.getState().activeModifier,
    vitals: useSimStore.getState().vitals,
  }));
  // ── Gated recovery ──────────────────────────────────────────────────────────
  // Some scenarios (LAST, hyperkalemic arrest) stay refractory to defib/pressors
  // until a cause-specific antidote has been given AND allowed to circulate.
  // Track the earliest administration time of every intervention (persistent
  // across each intervention's own expiry), and a transient "no response" notice.
  const [antidoteTimes, setAntidoteTimes] = useState({});
  const antidoteTimesRef = useRef(antidoteTimes);
  antidoteTimesRef.current = antidoteTimes;
  const relevanceCtxRef = useRef(relevanceCtx);
  relevanceCtxRef.current = relevanceCtx;
  const [gateNotice, setGateNotice] = useState(null); // { text, t }

  useEffect(() => {
    if (activeScenario && PANEL_SCENARIO_KEYS.has(activeScenario)) return; // shock/CPR — keep palette anchored
    setRelevanceCtx({
      scenario: activeScenario,
      modifier: useSimStore.getState().activeModifier,
      vitals: useSimStore.getState().vitals,
    });
    // A new patient scenario is a fresh case: reset the antidote history and any
    // stale "no response" notice so the recovery gate starts closed again.
    setAntidoteTimes({});
    setGateNotice(null);
  }, [activeScenario, store.activeModifier]);

  // Auto-dismiss the transient gate notice a few seconds after it appears.
  useEffect(() => {
    if (!gateNotice) return;
    const id = setTimeout(() => setGateNotice(null), 8000);
    return () => clearTimeout(id);
  }, [gateNotice]);

  // Effective settle time — Instant transition speed (0) skips the realism delay.
  const gateSettleMs = useCallback(
    (gate) => (useSimStore.getState().transitionDuration === 0 ? 0 : (gate?.settleMs ?? 0)),
    []
  );

  const toggleGroup = useCallback((label) => {
    setOpenGroups((prev) => {
      const next = new Set(prev);
      next.has(label) ? next.delete(label) : next.add(label);
      return next;
    });
  }, []);

  // Local state: active interventions with countdown data
  // Shape: [{ id, key, appliedDeltas, expiresAt }]
  const [activeInterventions, setActiveInterventions] = useState([]);
  // Selected dose preset per drug (key → dose object); defaults from the data layer.
  const [doseByKey, setDoseByKey] = useState({});
  const doseFor = useCallback((key) => doseByKey[key] ?? defaultDose(key), [doseByKey]);

  // Force re-render every second to refresh countdown displays
  const [, tick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => tick((n) => n + 1), 1000);
    return () => clearInterval(id);
  }, []);

  // Timeout handle registry — keyed by intervention id
  const timerRef = useRef({});

  // ── Wear-off / cancel: undo exactly what a dose applied ────────────────────
  // Uses the recorded per-vital deltas (so dose scaling and saturation revert
  // symmetrically) and ramps DOWN over the drug's offset time instead of
  // snapping — a pressor fades, it doesn't vanish. Instant speed = immediate.
  const revertEntry = useCallback((entry) => {
    const liveState = useSimStore.getState();
    const intervention = INTERVENTIONS[entry.key];
    const speedFactor = liveState.transitionDuration / 30000;
    const offsetMs = Math.round((intervention?.offsetMs ?? Math.max(60_000, (intervention?.transitionMs ?? 30_000) * 2)) * speedFactor);
    liveState.applyVitals(revertAppliedDeltas(liveState.vitals, entry.appliedDeltas), offsetMs);
    const ventRevert = computeVentTargets(liveState.ventSettings, entry.key, entry.appliedModifier, -1, entry.appliedScenario, entry.appliedOverride, entry.scale ?? 1);
    if (ventRevert) liveState.setVentSettings({ ...ventRevert, ...(entry.priorVentAbs ?? {}), ...(intervention?.ventOff ?? {}) });
    // Revert capnogram and rhythm to what they were before the drug was given
    if (entry.priorCapno != null) liveState.setCapnoMorphology(entry.priorCapno);
    if (entry.priorRhythm != null) liveState.setRhythm(entry.priorRhythm);
    // Revert channel visibility (e.g. extubation hides EtCO₂ if it wasn't shown before)
    if (entry.priorChannelVisibility) {
      Object.entries(entry.priorChannelVisibility).forEach(([ch, wasVisible]) => liveState.setChannelVisible(ch, wasVisible));
    }
    // Extubation — restore prior airway status
    if (entry.key === 'intubation') liveState.setIntubated(entry.priorIntubated ?? false);
  }, []);

  // Schedule the auto-revert for an entry (new, or re-hydrated after a refresh).
  const scheduleEntry = useCallback((entry) => {
    const remaining = Math.max(0, entry.expiresAt - Date.now());
    timerRef.current[entry.id] = setTimeout(() => {
      revertEntry(entry);
      setActiveInterventions((prev) => prev.filter((i) => i.id !== entry.id));
      delete timerRef.current[entry.id];
    }, remaining);
  }, [revertEntry]);

  // ── Administer intervention ────────────────────────────────────────────────
  const handleIntervention = useCallback(
    (interventionKey) => {
      const intervention = INTERVENTIONS[interventionKey];
      if (!intervention) return;

      // Gated recovery: if the active scenario is refractory and this drug is one
      // of the blocked ACLS actions, it produces NO effect until the antidote has
      // been given and circulated. Antidotes themselves are never in `blocks`, so
      // they pass through and open the gate.
      const gate = SCENARIOS[relevanceCtxRef.current.scenario]?.recoveryGate ?? null;
      if (isRecoveryBlocked(gate, interventionKey, antidoteTimesRef.current, Date.now(), gateSettleMs(gate))) {
        const st = recoveryGateStatus(gate, antidoteTimesRef.current, Date.now(), gateSettleMs(gate));
        store.recordCaseEvent(eventKindFor(interventionKey), `${intervention.name} — no effect (refractory)`);
        setGateNotice({
          text: st.anyGiven
            ? `${intervention.name}: no effect — antidote still circulating (${formatCountdown(st.settleRemainingMs)} to effect). ${gate.hint}`
            : `${intervention.name}: no effect — ${gate.hint}`,
          t: Date.now(),
        });
        return; // blocked — do not apply vitals / start a timed effect
      }

      // Record the earliest administration time (persists past this dose's expiry)
      // so the recovery gate stays open once an antidote has been given.
      setAntidoteTimes((prev) => (prev[interventionKey] ? prev : { ...prev, [interventionKey]: Date.now() }));

      const dose = doseFor(interventionKey);
      const scale = dose?.scale ?? 1;
      store.recordCaseEvent(eventKindFor(interventionKey), dose ? `${intervention.name} ${dose.label}` : intervention.name);

      const { vitals, activeModifier, activeScenario: liveScenario, capnoMorphology: snapCapno, rhythm: snapRhythm, visibleChannels: snapChannels, transitionDuration } = useSimStore.getState();
      // CPR / shock / ECMO are pseudo-scenarios layered on top of the patient's real
      // pathology. A drug given during compressions must resolve its scenario-specific
      // effects against that pathology (e.g. insulin+D50 in hyperkalaemia), not against
      // "High Quality CPR" — otherwise every scenarioDelta silently misses. The
      // relevance context already holds the anchored patient scenario for this reason.
      const snapScenario = PANEL_SCENARIO_KEYS.has(liveScenario)
        ? (relevanceCtxRef.current.scenario ?? liveScenario)
        : liveScenario;
      // A custom scenario can define this intervention's response in its JSON;
      // that override wins over the built-in base/scenario deltas.
      const override = customScenarios.find((c) => c.key === snapScenario)?.interventions?.[interventionKey] ?? null;
      const { targets, applied: appliedDeltas } = computeInterventionEffect(vitals, interventionKey, activeModifier, +1, snapScenario, override, scale);
      // Scale each drug's natural onset by the instructor's global transition
      // speed (30 s slider position = the drug's reference rate). This lets the
      // Transition Speed slider govern drugs as well as scenarios; 0 = instant.
      const speedFactor = transitionDuration / 30000;
      const drugMs = intervention.transitionMs != null
        ? Math.round(intervention.transitionMs * speedFactor)
        : undefined;
      store.applyVitals(targets, drugMs);

      // Airway-mechanics side-effect (e.g. bronchodilators in bronchospasm lower
      // Ppeak and restore VTe; decompression relieves a tension PTX). Applied
      // directly to ventSettings; reverted with sign -1 on expiry / cancel.
      const ventBefore = useSimStore.getState().ventSettings;
      const ventNext = computeVentTargets(ventBefore, interventionKey, activeModifier, +1, snapScenario, override, scale);
      if (ventNext) store.setVentSettings(ventNext);
      // Vaporizer / fresh-gas settings are absolute, so remember what they were
      // (e.g. agent 'none', FiO₂ 100) to restore when the agent is turned off.
      let priorVentAbs = null;
      if (ventNext) {
        VENT_ABSOLUTE_KEYS.forEach((k) => {
          if (ventNext[k] !== ventBefore[k]) (priorVentAbs ??= {})[k] = ventBefore[k];
        });
      }

      // Needle decompression: cue the MONITOR window to play the air-release hiss
      if (interventionKey === 'needleDecompression') {
        store.setAudioCue({ type: 'needleRelease', id: Date.now() });
      }

      // Apply capnoMorphology side-effect if the scenario delta specifies one
      // (e.g. epinephrine in anaphylaxis normalises the shark-fin waveform).
      const appliedCapno = getInterventionCapnoMorphology(interventionKey, activeModifier, snapScenario, override);
      if (appliedCapno) setCapnoMorphology(appliedCapno);

      // Apply rhythm side-effect if the scenario delta specifies one
      // (e.g. insulin + D50W in hyperkalemia reverts the ECG to normal sinus).
      // While compressions run the ECG shows artifact, so a drug that would fix the
      // rhythm (e.g. insulin + D50W in hyperkalaemia) updates the rhythm HIDDEN
      // beneath it — the next rhythm check reveals the improvement.
      const appliedRhythm = getInterventionRhythm(interventionKey, activeModifier, snapScenario, override);
      if (appliedRhythm) {
        if (cprActiveRef.current) setUnderlyingRhythm(appliedRhythm);
        else setRhythm(appliedRhythm);
      }

      // Apply channel visibility side-effects (e.g. intubation enables EtCO₂ row).
      // Capture the prior state of each affected channel so we can restore it on
      // cancel / expiry (represents extubation or removal of the equipment).
      let priorChannelVisibility = null;
      if (intervention.channelEffects) {
        priorChannelVisibility = {};
        Object.entries(intervention.channelEffects).forEach(([channel, visible]) => {
          priorChannelVisibility[channel] = snapChannels[channel] ?? false;
          store.setChannelVisible(channel, visible);
        });
      }

      // Securing the airway marks the patient as intubated so the ventilator
      // screen shows a mechanically ventilated patient (agent/MAC, live airway
      // waves) instead of the STANDBY view. Reverted on extubation (cancel/expiry).
      const priorIntubated = store.intubated;
      if (interventionKey === 'intubation') store.setIntubated(true);

      const id = `${interventionKey}-${Date.now()}`;
      const expiresAt = Date.now() + intervention.duration;

      // Capture context at administration time so the revert uses the same deltas
      const appliedModifier = activeModifier;
      const appliedScenario = snapScenario;
      // Capture morphology/rhythm *before* changes so we can revert on expiry
      const priorCapno = appliedCapno ? snapCapno : null;
      const priorRhythm = appliedRhythm ? snapRhythm : null;

      const entry = {
        id, key: interventionKey, appliedModifier, appliedScenario, appliedOverride: override, scale,
        doseLabel: dose?.label ?? null, appliedDeltas,
        priorCapno, priorRhythm, priorChannelVisibility, priorIntubated, priorVentAbs, expiresAt,
      };
      setActiveInterventions((prev) => [...prev, entry]);
      scheduleEntry(entry);
    },
    [store, setCapnoMorphology, setRhythm, customScenarios, doseFor, scheduleEntry]
  );

  // ── Cancel intervention early ──────────────────────────────────────────────
  const handleCancelIntervention = useCallback(
    (entry) => {
      clearTimeout(timerRef.current[entry.id]);
      delete timerRef.current[entry.id];
      revertEntry(entry);
      setActiveInterventions((prev) => prev.filter((i) => i.id !== entry.id));
    },
    [revertEntry]
  );

  // ── Survive a refresh ──────────────────────────────────────────────────────
  // Running drugs (with their expiry), antidote times, the shock log and dose
  // picks are mirrored to localStorage; on mount they are re-hydrated and their
  // auto-revert timers re-armed for the REMAINING time. The vitals they produced
  // come back via the store snapshot (see InstructorPanel). Entries that expired
  // while the tab was closed are reverted immediately.
  const hydratedRef = useRef(false);
  useEffect(() => {
    if (hydratedRef.current) return;
    hydratedRef.current = true;
    try {
      const saved = JSON.parse(localStorage.getItem(STATE_KEY) || 'null');
      if (!saved || Date.now() - (saved.savedAt ?? 0) > STATE_MAX_AGE_MS) return;
      const live = (saved.activeInterventions ?? []).filter((e) => e.expiresAt > Date.now() && INTERVENTIONS[e.key]);
      (saved.activeInterventions ?? []).filter((e) => e.expiresAt <= Date.now() && INTERVENTIONS[e.key]).forEach(revertEntry);
      setActiveInterventions(live);
      live.forEach(scheduleEntry);
      if (saved.antidoteTimes) setAntidoteTimes(saved.antidoteTimes);
      if (saved.shockLog) setShockLog(saved.shockLog);
      if (saved.doseByKey) setDoseByKey(saved.doseByKey);
    } catch { /* corrupt / unavailable storage — start clean */ }
  }, [revertEntry, scheduleEntry]);
  useEffect(() => {
    if (!hydratedRef.current) return;
    try {
      localStorage.setItem(STATE_KEY, JSON.stringify({ savedAt: Date.now(), activeInterventions, antidoteTimes, shockLog, doseByKey }));
    } catch { /* ignore */ }
  }, [activeInterventions, antidoteTimes, shockLog, doseByKey]);

  // Clean up all timers on unmount
  useEffect(() => {
    return () => {
      Object.values(timerRef.current).forEach(clearTimeout);
      clearTimeout(rhythmCheckTimerRef.current);
    };
  }, []);

  // ── "Normal Baseline" reset ────────────────────────────────────────────────
  // ScenarioSelector bumps `interventionResetToken`. Cancel every running
  // intervention WITHOUT applying its revert deltas — the baseline scenario has
  // already set the vitals, so reverting on top would push them off baseline.
  // Just kill the pending auto-revert timers and clear the derived panel state.
  const resetToken = useSimStore((s) => s.interventionResetToken);
  const didMountResetRef = useRef(false);
  useEffect(() => {
    if (!didMountResetRef.current) { didMountResetRef.current = true; return; }
    Object.values(timerRef.current).forEach(clearTimeout);
    timerRef.current = {};
    clearTimeout(rhythmCheckTimerRef.current);
    setActiveInterventions([]);
    setShockLog([]);
    setAntidoteTimes({});
    setGateNotice(null);
    setUnderlyingRhythm(null);
    setRhythmCheckUntil(0);
  }, [resetToken]);

  // ── Recompute TOF + BIS whenever active interventions change ─────────────
  useEffect(() => {
    const keys = activeInterventions.map((i) => i.key);

    // TOF: reversal agent wins (sugammadex > neostigmine); else deepest block; else unblocked
    let tofCount = 4;
    let tofRatio = 100;
    const reversal = keys.includes('sugammadex') ? 'sugammadex' : keys.includes('neostigmine') ? 'neostigmine' : null;
    if (reversal) {
      tofCount = TOF_EFFECTS[reversal].count;
      tofRatio = TOF_EFFECTS[reversal].ratio;
    } else {
      const activeParalytic = ['succinylcholine', 'rocuronium', 'cisatracurium'].find((k) => keys.includes(k));
      if (activeParalytic) {
        tofCount = TOF_EFFECTS[activeParalytic].count;
        tofRatio = TOF_EFFECTS[activeParalytic].ratio;
      }
    }
    setTof(tofCount, tofRatio);

    // BIS: start at 98 (awake), sum EEG-slowing sedative offsets...
    let bis = 98;
    Object.entries(BIS_EFFECTS).forEach(([drug, delta]) => {
      if (keys.includes(drug)) bis += delta;
    });
    // ...then apply ketamine's paradoxical EEG-activation effect on top:
    // it raises a deep BIS toward ~60+ (and is unreliable in general).
    if (keys.includes('ketamine')) {
      bis = Math.max(bis + KETAMINE_BIS.offset, KETAMINE_BIS.floor);
    }
    setBIS(Math.max(20, Math.min(99, bis)));
  }, [activeInterventions, setTof, setBIS]);

  // Per-group button color class lookup
  const groupColorClass = {
    'Vasopressors & Inotropes': styles.btnVasopressor,
    'Antiarrhythmics & Rate Control': styles.btnRateControl,
    'Antihypertensives & Vasodilators': styles.btnRateControl,
    'Paralytics': styles.btnParalytic,
    'Sedatives': styles.btnSedative,
    'Inhalational Agents': styles.btnSedative,
    'Bronchodilators': styles.btnBronchodilator,
    'Obstetric': styles.btnObstetric,
    'Reversal Agents': styles.btnReversal,
    'Analgesics': styles.btnAnalgesic,
    'Benzodiazepines': styles.btnSedative,
  };

  // Per-key overrides that take precedence over the group color
  const keyColorClass = {
    calciumChloride: styles.btnSedative,   // yellow — distinct from other electrolytes
    sugammadex: styles.btnSugammadex,      // diagonal stripe — reversal agent
    // Inhalational agents follow the vaporizer colour code (matches the Aisys screen)
    sevoflurane:  styles.btnAgentSevo,     // yellow
    desflurane:   styles.btnAgentDes,      // blue
    isoflurane:   styles.btnAgentIso,      // purple
    nitrousOxide: styles.btnAgentN2o,      // blue
  };

  // Map activeInterventions by key for quick lookup in the button renderer
  const activeByKey = activeInterventions.reduce((acc, entry) => {
    if (!acc[entry.key]) acc[entry.key] = [];
    acc[entry.key].push(entry);
    return acc;
  }, {});

  const now = Date.now();

  // Standing recovery-gate advisory for the active patient scenario (if gated).
  const recoveryGate = SCENARIOS[relevanceCtx.scenario]?.recoveryGate ?? null;
  const gateEffectiveSettle = transitionDuration === 0 ? 0 : (recoveryGate?.settleMs ?? 0);
  const gateStatus = recoveryGate
    ? recoveryGateStatus(recoveryGate, antidoteTimes, now, gateEffectiveSettle)
    : null;

  // Relevance filter: when a scenario is active (and not overridden), show only
  // interventions indicated for it — plus any drug currently running. Uses the
  // FROZEN relevanceCtx so the palette only changes on scenario selection, not
  // when an intervention (drug, shock, CPR) alters vitals or activeScenario.
  const filterActive = !!relevanceCtx.scenario && !showAllDrugs;
  // A custom scenario can name the interventions it responds to; surface those too.
  const relevanceCustomIvs = customScenarios.find((c) => c.key === relevanceCtx.scenario)?.interventions;
  const relevantKeys = (keys) =>
    filterActive
      ? keys.filter((k) => activeByKey[k]?.length || relevanceCustomIvs?.[k] || isInterventionRelevant(k, relevanceCtx.scenario, relevanceCtx.modifier, relevanceCtx.vitals))
      : keys;

  // Group interventions by ACLS algorithm category
  const groups = [
    {
      // ── CPR quality modes & V-A ECMO ──────────────────────────────────────
      // These are scenario-driven modes (not timed drug interventions) so they
      // use scenarioKeys instead of keys.  Pressing one applies absolute vitals +
      // rhythm without a countdown; the active button highlights to show the
      // current resuscitation state.  Press any other scenario to exit the mode.
      label: 'CPR & Resuscitation',
      keys: [],
      scenarioKeys: ['cprHighQuality', 'cprLowQuality', 'lucasCPR', 'vaECMO', 'iabp', 'impella'],
    },
    {
      label: 'Fluids & Hemostasis',
      keys: ['ivFluid', 'bloodTransfusion', 'warmedIVFluid', 'tranexamicAcid', 'massiveTransfusion', 'pelvicBinder', 'irEmbolization', 'reboa'],
    },
    {
      label: 'Vasopressors & Inotropes',
      keys: ['epinephrine', 'norepinephrine', 'vasopressin', 'dopamine', 'dobutamine', 'phenylephrine', 'ephedrine', 'atropine'],
    },
    {
      // Vaughan-Williams antiarrhythmics incl. class II (β-blockers) and
      // class IV (Ca-channel blockers). Atropine is an anticholinergic
      // chronotrope, NOT an antiarrhythmic — it lives with the pressors.
      label: 'Antiarrhythmics & Rate Control',
      keys: ['adenosine', 'amiodarone', 'lidocaine', 'magnesium', 'esmolol', 'metoprolol', 'diltiazem', 'verapamil'],
    },
    {
      label: 'Antihypertensives & Vasodilators',
      keys: ['labetalol', 'phentolamine', 'nitroglycerin', 'hydralazine', 'nitroprusside'],
    },
    {
      label: 'Endocrine & Glucose',
      keys: ['insulinInfusion', 'dextrose50', 'levothyroxine', 'octreotide', 'cyproheptadine', 'bromocriptine'],
    },
    {
      label: 'Bronchodilators',
      keys: ['albuterol'],
    },
    {
      label: 'Obstetric',
      keys: ['oxytocin', 'terbutaline', 'perimortemHysterotomy'],
    },
    {
      label: 'Electrolytes & Resuscitation',
      keys: ['sodiumBicarb', 'calciumChloride', 'mannitol', 'hypertonicSaline'],
    },
    {
      label: 'Airway & Ventilation',
      keys: ['venturiMask', 'nonRebreather', 'intubation', 'cricothyrotomy', 'needleDecompression', 'thoracostomy', 'chestTube', 'hyperventilation'],
    },
    {
      label: 'Maneuvers & Positioning',
      keys: ['passiveLegRaise', 'vagalManeuver', 'recruitmentManeuver', 'proning',
             'durantsManeuver', 'leftUterineDisplacement', 'headUpPosition', 'bronchoscopy'],
    },
    {
      label: 'Analgesics',
      keys: ['fentanyl', 'remifentanil', 'hydromorphone', 'morphine'],
    },
    {
      label: 'Sedatives',
      keys: ['propofol', 'ketamine', 'dexmedetomidine'],
    },
    {
      label: 'Inhalational Agents',
      keys: ['sevoflurane', 'desflurane', 'isoflurane', 'nitrousOxide'],
    },
    {
      label: 'Benzodiazepines',
      keys: ['midazolam', 'lorazepam'],
    },
    {
      label: 'Paralytics',
      keys: ['succinylcholine', 'rocuronium', 'cisatracurium', 'sugammadex'],
    },
    {
      label: 'Reversal Agents',
      keys: ['naloxone', 'flumazenil', 'neostigmine'],
    },
    {
      label: 'Other Medications',
      keys: ['broadSpectrumAntibiotics', 'insulinD50', 'corticosteroid', 'dantrolene', 'intralipid', 'methyleneBlue', 'antihistamine', 'furosemide', 'inhaledPulmonaryVasodilator'],
    },
    {
      label: 'Special Equipment',
      keys: ['bodyWarmer', 'activeCooling', 'transcutaneousPacemaker', 'pericardiocentesis', 'dialysis'],
      scenarioKeys: ['synchronizedCardioversion', 'defibrillation'],
    },
  ];

  return (
    <div className={styles.panel}>
      {/* ── Gated-recovery advisory (LAST, hyperkalemic arrest, …) ───────── */}
      {recoveryGate && gateStatus && (
        <div className={`${styles.gateStrip} ${gateStatus.open ? styles.gateOpen : styles.gateClosed}`}>
          {gateStatus.open ? (
            <span>✓ Antidote circulating — defibrillation / pressors will now restore output.</span>
          ) : gateStatus.anyGiven ? (
            <span>⏳ Antidote given — effective in {formatCountdown(gateStatus.settleRemainingMs)}. Standard ACLS is still refractory.</span>
          ) : (
            <span>⚠ Refractory to defibrillation &amp; pressors. {recoveryGate.hint}</span>
          )}
        </div>
      )}
      {/* Transient "no response" notice on a blocked action attempt */}
      {gateNotice && (
        <div className={styles.gateNotice} role="status">{gateNotice.text}</div>
      )}

      {/* ── Capnogram morphology ────────────────────────────────────────── */}
      <div className={styles.morphologySection}>
        <button
          className={styles.groupHeader}
          onClick={() => toggleGroup('Capnogram Morphology')}
        >
          <span className={styles.groupLabel}>Capnogram Morphology<HelpTip label="About capnogram morphology">Change the shape of the capnography waveform independently of the scenario — e.g. obstructive "shark fin" for bronchospasm, curare cleft for inadequate paralysis, or a rising baseline for rebreathing.</HelpTip></span>
          <span className={styles.groupChevron}>
            {openGroups.has('Capnogram Morphology') ? '▾' : '▸'}
          </span>
        </button>
        {openGroups.has('Capnogram Morphology') && (
          <div className={styles.morphologyGrid}>
            {Object.entries(CAPNO_MORPHOLOGIES).map(([key, morph]) => (
              <button
                key={key}
                className={`${styles.morphologyButton} ${capnoMorphology === key ? styles.morphologyActive : ''}`}
                onClick={() => setCapnoMorphology(key)}
                title={morph.description}
              >
                <span className={styles.morphLabel}>{morph.label}</span>
                <span className={styles.morphDesc}>{morph.description}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* ── Relevance filter bar (anchored to the selected patient scenario) ── */}
      {/* Resolve the name from built-in OR custom scenarios so custom scenarios
          also get the "Show all" toggle (otherwise they trap the filter on). */}
      {relevanceCtx.scenario && (SCENARIOS[relevanceCtx.scenario] || customScenarios.some((c) => c.key === relevanceCtx.scenario)) && (
        <div className={styles.filterBar}>
          <span className={styles.filterText}>
            {showAllDrugs
              ? 'Showing all interventions'
              : `Showing drugs relevant to ${SCENARIOS[relevanceCtx.scenario]?.name ?? customScenarios.find((c) => c.key === relevanceCtx.scenario)?.name}`}
          </span>
          <button
            className={styles.filterToggle}
            onClick={() => setShowAllDrugs((v) => !v)}
          >
            {showAllDrugs ? 'Relevant only' : 'Show all'}
          </button>
        </div>
      )}

      {/* ── Rhythm check — pause compressions to reveal the underlying rhythm ─ */}
      {cprActive && (() => {
        const checking = rhythmCheckUntil > now;
        const reveal = underlyingRhythm ?? 'asystole';
        const shockable = SHOCKABLE.has(reveal);
        return (
          <div className={`${styles.rhythmCheckBar} ${checking ? styles.rhythmCheckBarActive : ''}`}>
            <span className={styles.rhythmCheckTitle}>CPR in progress<HelpTip label="About rhythm checks">During compressions the ECG shows CPR artifact. <strong>Rhythm Check</strong> pauses compressions for 6 seconds to reveal the underlying rhythm and whether it is shockable, then CPR resumes automatically.</HelpTip></span>
            {checking ? (
              <span className={styles.rhythmCheckResult}>
                Rhythm check: <strong>{rhythmLabel(reveal)}</strong>
                <span className={shockable ? styles.rhythmShockable : styles.rhythmNonShockable}>
                  {shockable ? 'SHOCKABLE' : 'NON-SHOCKABLE'}
                </span>
                <span className={styles.rhythmCheckCountdown}>
                  resuming in {Math.max(0, Math.ceil((rhythmCheckUntil - now) / 1000))}s
                </span>
              </span>
            ) : (
              <>
                <span className={styles.rhythmCheckHint}>
                  ECG shows compression artifact — pause to see the real rhythm.
                </span>
                <button className={styles.rhythmCheckBtn} onClick={doRhythmCheck}>
                  ⏸ Rhythm Check
                </button>
              </>
            )}
          </div>
        );
      })()}

      {/* ── Shock log — timestamped cardioversion / defibrillation events ──── */}
      {shockLog.length > 0 && (
        <div className={styles.shockLog}>
          <span className={styles.shockLogTitle}>⚡ Shocks ({shockLog.length})<HelpTip label="About the shock log">Time and energy of each defibrillation or cardioversion delivered, newest first. The monitor shows the same tally.</HelpTip></span>
          <div className={styles.shockLogEntries}>
            {shockLog.slice(0, 8).map((s, i) => (
              <span key={`${s.t}-${i}`} className={styles.shockLogEntry}>
                <span className={styles.shockLogTime}>{new Date(s.t).toLocaleTimeString([], { hour12: false })}</span>
                <span className={styles.shockLogType}>{s.type === 'defibrillation' ? 'Defib' : 'Cardiovert'}</span>
                {s.energy != null && <span className={styles.shockLogEnergy}>{s.energy} J</span>}
              </span>
            ))}
          </div>
          <button className={styles.shockLogClear} onClick={() => setShockLog([])}>Clear</button>
        </div>
      )}

      {/* ── Intervention groups ──────────────────────────────────────────── */}
      {groups.map((group) => {
        const keys = relevantKeys(group.keys ?? []);
        // Hide a group entirely if the filter removed all its drugs and it has
        // no scenario-procedure buttons.
        if (keys.length === 0 && !(group.scenarioKeys?.length)) return null;
        return (
        <div key={group.label} className={styles.group}>
          <button
            className={styles.groupHeader}
            onClick={() => toggleGroup(group.label)}
          >
            <span className={styles.groupLabel}>{group.label}</span>
            <span className={styles.groupChevron}>
              {openGroups.has(group.label) ? '▾' : '▸'}
            </span>
          </button>
          {openGroups.has(group.label) && <div className={styles.buttonGrid}>
            {keys.map((key) => {
              const intervention = INTERVENTIONS[key];
              const activeEntries = activeByKey[key] || [];
              const isActive = activeEntries.length > 0;

              // Show the soonest-expiring entry's countdown
              const soonest = activeEntries.reduce(
                (min, e) => (e.expiresAt < (min?.expiresAt ?? Infinity) ? e : min),
                null
              );

              return (
                <div key={key} className={styles.interventionWrapper}>
                  {/* Compact card: name row only; chevron expands details in place.
                      Clicking the card body still administers the drug (the chevron
                      stops propagation). The desktop hover tooltip keeps the full
                      description without expanding. */}
                  <button
                    className={`${styles.interventionButton} ${keyColorClass[key] ?? groupColorClass[group.label] ?? ''} ${isActive ? styles.interventionActive : ''}`}
                    onClick={() => handleIntervention(key)}
                    title={`${intervention.name} — ${intervention.description}`}
                  >
                    <span className={styles.drugNameRow}>
                      <span className={styles.drugName}>{intervention.name}</span>
                      {intervention.doses && !expandedCards.has(key) && (
                        <span className={styles.doseTag}>{doseFor(key)?.label}</span>
                      )}
                      <span
                        className={styles.expandChevron}
                        role="button"
                        aria-label={expandedCards.has(key) ? 'Hide details' : 'Show details'}
                        onClick={(e) => { e.stopPropagation(); toggleCard(key); }}
                      >
                        {expandedCards.has(key) ? '▾' : '▸'}
                      </span>
                    </span>
                    {expandedCards.has(key) && (
                      <>
                        {intervention.doses && (
                          <span className={styles.energyRow} onClick={(e) => e.stopPropagation()}>
                            <span className={styles.energyLabel}>Dose</span>
                            {intervention.doses.map((d) => (
                              <span
                                key={d.label}
                                role="button"
                                className={`${styles.energyChip} ${styles.doseChip} ${doseFor(key)?.label === d.label ? styles.energyChipActive : ''}`}
                                onClick={(e) => { e.stopPropagation(); setDoseByKey((m) => ({ ...m, [key]: d })); }}
                                title={`Give ${intervention.name} ${d.label}`}
                              >
                                {d.label}
                              </span>
                            ))}
                          </span>
                        )}
                        <span className={styles.drugEffect}>{intervention.description}</span>
                        <span className={styles.drugMeta}>
                          <span className={styles.metaItem}>
                            <span className={styles.metaLabel}>Onset</span>
                            <span className={styles.metaValue}>{intervention.onset}</span>
                          </span>
                          <span className={styles.metaDivider}>·</span>
                          <span className={styles.metaItem}>
                            <span className={styles.metaLabel}>Duration</span>
                            <span className={styles.metaValue}>{intervention.durationLabel}</span>
                          </span>
                        </span>
                      </>
                    )}
                    {isActive && (
                      <span className={styles.countdown}>
                        ⏱ {formatCountdown(soonest.expiresAt - now)} remaining
                      </span>
                    )}
                  </button>
                  {/* Cancel buttons for each active dose */}
                  {activeEntries.map((entry) => (
                    <button
                      key={entry.id}
                      className={styles.cancelButton}
                      onClick={() => handleCancelIntervention(entry)}
                      title="Cancel / wear off now"
                    >
                      ✕ {formatCountdown(entry.expiresAt - now)}
                    </button>
                  ))}
                </div>
              );
            })}
            {/* Scenario-triggered procedure buttons (no countdown / auto-revert) */}
            {group.scenarioKeys?.map((key) => {
              const scenario = SCENARIOS[key];
              if (!scenario) return null;
              const isActive = activeScenario === key;
              const isShock = SHOCK_SCENARIOS.has(key);
              const energy = shockEnergy[key] ?? DEFAULT_SHOCK_ENERGY[key];
              return (
                <div key={key} className={styles.interventionWrapper}>
                  <button
                    className={`${styles.interventionButton} ${isActive ? styles.interventionActive : ''}`}
                    onClick={() => handleScenario(key)}
                    title={scenario.description ?? scenario.name}
                  >
                    <span className={styles.drugName}>{scenario.name}</span>
                    {isShock ? (
                      <span className={styles.drugEffect}>⚡ Deliver <strong>{energy} J</strong></span>
                    ) : scenario.subtitle && (
                      <span className={styles.drugEffect}>{scenario.subtitle}</span>
                    )}
                  </button>
                  {isShock && (
                    <div className={styles.energyRow}>
                      <span className={styles.energyLabel}>Energy<HelpTip label="About shock energy">Pick the energy before delivering the shock. The chosen joules are logged and shown on the monitor.</HelpTip></span>
                      {SHOCK_ENERGY[key].map((j) => (
                        <button
                          key={j}
                          className={`${styles.energyChip} ${energy === j ? styles.energyChipActive : ''}`}
                          onClick={() => setShockEnergy((e) => ({ ...e, [key]: j }))}
                          title={`Set ${scenario.name} energy to ${j} J`}
                        >
                          {j}
                        </button>
                      ))}
                      <span className={styles.energyUnit}>J</span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>}
        </div>
        );
      })}
    </div>
  );
}
